import {
  BoltIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FaceSmileIcon,
  LightBulbIcon,
  MagnifyingGlassIcon,
  RectangleStackIcon,
  SignalIcon,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon,
  MinusCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

export const SECTIONS = [
  { id: "reports", label: "All Audits", icon: DocumentTextIcon },
  { id: "issues", label: "Issues", icon: ExclamationTriangleIcon },
  { id: "pages", label: "Pages", icon: RectangleStackIcon },
  { id: "suggestions", label: "Suggestions", icon: LightBulbIcon },
];

export const SIDEBAR_TYPES = ["demand", "page_audit", "page_speed", "ux_audit", "indexing"];

export const TYPE_LABELS = {
  demand: "Demand",
  page_audit: "Page audit",
  page_speed: "Page speed",
  ux_audit: "UX audit",
  indexing: "Indexing",
};

export const TYPE_ICONS = {
  demand: MagnifyingGlassIcon,
  page_audit: DocumentTextIcon,
  page_speed: BoltIcon,
  ux_audit: FaceSmileIcon,
  indexing: SignalIcon,
};

export const RUNNABLE_TYPES = [
  {
    id: "page_audit",
    label: "Page audit",
    description: "Scan every page for titles, meta, content, and technical SEO problems.",
  },
  {
    id: "demand",
    label: "Demand",
    description: "Estimate monthly search demand for your pages and keyword opportunities.",
  },
  {
    id: "page_speed",
    label: "Page speed",
    description: "Check Core Web Vitals from real visitor data (LCP, INP, CLS).",
  },
  {
    id: "ux_audit",
    label: "UX audit",
    description: "Review pages for clarity, helpfulness, and confusing copy or next steps.",
  },
  {
    id: "indexing",
    label: "Indexing",
    description: "See what Google can crawl and index.",
    disabled: true,
    note: "Coming soon",
  },
];

export const PAGE_FILTERS = [
  { id: "all", label: "All" },
  { id: "fail", label: "Fail" },
  { id: "warn", label: "Warn" },
  { id: "pass", label: "Pass" },
  { id: "skipped", label: "Skipped" },
];

export const STATUS_META = {
  pass: { label: "Pass", dot: "bg-emerald-500", text: "text-emerald-500", fill: "#10b981", Icon: CheckCircleIcon },
  warn: { label: "Warn", dot: "bg-amber-500", text: "text-amber-500", fill: "#f59e0b", Icon: ExclamationTriangleIcon },
  fail: { label: "Fail", dot: "bg-red-500", text: "text-red-500", fill: "#ef4444", Icon: XCircleIcon },
  skipped: { label: "Skipped", dot: "bg-theme-shadow", text: "text-theme-secondary-text", fill: "#94a3b8", Icon: MinusCircleIcon },
};

export const STATUS_ORDER = { fail: 0, warn: 1, skipped: 2, pass: 3 };

/** CWV thresholds: [goodMax, needsImprovementMax]. Above needs = poor. Matches backend. */
export const VITAL_THRESHOLDS = {
  lcp: [2500, 4000],
  inp: [200, 500],
  cls: [0.1, 0.25],
  fcp: [1800, 3000],
  ttfb: [800, 1800],
};

/** Help copy + Google/web.dev docs for Core Web Vitals. */
export const VITAL_META = {
  lcp: {
    help: "Largest Contentful Paint: when the main content becomes visible. Aim for 2.5s or less.",
    docs: "https://web.dev/articles/lcp",
    stroke: "#6366f1",
  },
  inp: {
    help: "Interaction to Next Paint: how quickly the page responds to taps and clicks. Aim for 200ms or less.",
    docs: "https://web.dev/articles/inp",
    stroke: "#f59e0b",
  },
  cls: {
    help: "Cumulative Layout Shift: unexpected layout movement while loading. Aim for 0.1 or less.",
    docs: "https://web.dev/articles/cls",
    stroke: "#10b981",
  },
  fcp: {
    help: "First Contentful Paint: when the first text or image is painted. Aim for 1.8s or less.",
    docs: "https://web.dev/articles/fcp",
    stroke: "#8b5cf6",
  },
  ttfb: {
    help: "Time to First Byte: how long until the server starts responding. Aim for 800ms or less.",
    docs: "https://web.dev/articles/ttfb",
    stroke: "#06b6d4",
  },
};

export const REPORT_STATUS = {
  succeeded: "bg-emerald-500",
  failed: "bg-red-500",
  running: "bg-amber-500",
  queued: "bg-theme-shadow",
  canceled: "bg-theme-shadow",
};

export const CHART_TOOLTIP = {
  backgroundColor: "var(--theme-main-bg)",
  border: "1px solid var(--theme-shadow)",
  borderRadius: 8,
  fontSize: 12,
  color: "var(--theme-text)",
};

export const importanceBand = (score) => {
  if (score >= 70) return { label: "High", className: "bg-red-500/15 text-red-500 border-red-500/30" };
  if (score >= 40) return { label: "Medium", className: "bg-amber-500/15 text-amber-500 border-amber-500/30" };
  return { label: "Low", className: "bg-theme-secondary text-theme-secondary-text border-theme-secondary" };
};

/** Clickable list/table row: light shadow tint + radius. */
export const ROW_HOVER = "rounded-lg transition seo-row-hover";
